// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ResourceManager from '../utils/game/ResourceManager';
import CrewManager from '../utils/game/CrewManager';
import useEscapeKey from '../hooks/ui/useEscapeKey';
import './CargoManagementModal.css';

const CargoManagementModal = ({ onClose, embedded = false }) => {
  const [resources, setResources] = useState({});
  const [consumption, setConsumption] = useState({});
  const [daysRemaining, setDaysRemaining] = useState({});
  const [rationingMode, setRationingMode] = useState(false);
  const [crewCount, setCrewCount] = useState(0);
  const [modalFading, setModalFading] = useState(false);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 1000);
    return () => clearInterval(interval);
  }, []);

  const loadData = () => {
    const res = ResourceManager.getAllResources();
    const cons = ResourceManager.consumptionRates;
    const rationing = ResourceManager.isRationing();
    const crew = CrewManager.getAllCrew().length;

    // Calculate days remaining for each resource
    const days = {};
    Object.keys(res).forEach(resourceType => {
      days[resourceType] = ResourceManager.getDaysRemaining(resourceType, crew);
    });

    setResources(res);
    setConsumption(cons);
    setDaysRemaining(days);
    setRationingMode(rationing);
    setCrewCount(crew);
  };

  const handleClose = () => {
    setModalFading(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // Close on ESC key
  useEscapeKey(handleClose);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const toggleRationing = () => {
    ResourceManager.toggleRationing();
    loadData();
  };

  const getResourceColor = (days) => {
    if (days === Infinity) return '#00ff88';
    if (days > 5) return '#00ff88';
    if (days > 2) return '#ffaa00';
    return '#ff4444';
  };

  const getResourceStatus = (days) => {
    if (days === Infinity) return 'OPTIMAL';
    if (days > 5) return 'OK';
    if (days > 2) return 'LOW';
    return 'CRITICAL';
  };

  const getResourceIcon = (type) => {
    const icons = {
      food: '🍖',
      water: '💧',
      oxygen: '💨',
      medical: '⚕️',
      parts: '🔧'
    };
    return icons[type] || '📦';
  };

  const getResourceName = (type) => {
    const names = {
      food: 'FOOD',
      water: 'WATER',
      oxygen: 'OXYGEN',
      medical: 'MEDICAL',
      parts: 'SPARE PARTS'
    };
    return names[type] || type.toUpperCase();
  };

  const modalContent = (
    <div className="cargo-modal" onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      {!embedded && (
        <div className="cargo-header">
          <h2>◊ CARGO & RESOURCES ◊</h2>
          <button className="close-button" onClick={handleClose}>✕</button>
        </div>
      )}

        {/* Overview Stats */}
        <div className="cargo-overview">
          <div className="overview-card">
            <div className="overview-icon">👥</div>
            <div className="overview-info">
              <div className="overview-label">CREW SIZE</div>
              <div className="overview-value">{crewCount}</div>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-icon">📦</div>
            <div className="overview-info">
              <div className="overview-label">CARGO CAPACITY</div>
              <div className="overview-value">100 TONS</div>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-icon">{rationingMode ? '⚠️' : '✅'}</div>
            <div className="overview-info">
              <div className="overview-label">RATIONING</div>
              <div className="overview-value" style={{ color: rationingMode ? '#ffaa00' : '#00ff88' }}>
                {rationingMode ? 'ACTIVE' : 'NORMAL'}
              </div>
            </div>
          </div>
        </div>

        {/* Rationing Control */}
        <div className="rationing-control">
          <button 
            className={`rationing-button ${rationingMode ? 'active' : ''}`}
            onClick={toggleRationing}
          >
            {rationingMode ? '⚠️ DISABLE RATIONING' : '✅ ENABLE RATIONING'}
          </button>
          <div className="rationing-info">
            {rationingMode 
              ? 'Rationing active: 50% reduced consumption, crew morale penalty'
              : 'Normal consumption: Full rations, optimal crew morale'
            }
          </div>
        </div>

        {/* Resources Grid */}
        <div className="resources-container">
          <h3>◊ LIFE SUPPORT RESOURCES ◊</h3>
          <div className="resources-grid">
            {Object.keys(resources).map((type) => {
              const amount = resources[type];
              const rate = consumption[type];
              const days = daysRemaining[type];
              const percentage = Math.min(100, (amount / 100) * 100);

              return (
                <div key={type} className="resource-card">
                  <div className="resource-header">
                    <div className="resource-icon">{getResourceIcon(type)}</div>
                    <div className="resource-info">
                      <div className="resource-name">{getResourceName(type)}</div>
                      <div 
                        className="resource-status"
                        style={{ color: getResourceColor(days) }}
                      >
                        {getResourceStatus(days)}
                      </div>
                    </div>
                    <div className="resource-amount">
                      {amount.toFixed(1)}
                    </div>
                  </div>

                  <div className="resource-bar">
                    <div 
                      className="resource-fill"
                      style={{ 
                        width: `${percentage}%`,
                        background: `linear-gradient(90deg, ${getResourceColor(days)}40, ${getResourceColor(days)})`,
                        boxShadow: `0 0 10px ${getResourceColor(days)}80`
                      }}
                    />
                  </div>

                  <div className="resource-details">
                    <div className="resource-detail">
                      <span className="detail-label">Consumption:</span>
                      <span className="detail-value">{rate.toFixed(2)}/day</span>
                    </div>
                    <div className="resource-detail">
                      <span className="detail-label">Days Remaining:</span>
                      <span 
                        className="detail-value"
                        style={{ color: getResourceColor(days) }}
                      >
                        {days === Infinity ? '∞' : days.toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        {!embedded && (
          <div className="cargo-footer">
            <button className="close-modal-button" onClick={handleClose}>
              CLOSE
            </button>
          </div>
        )}
      </div>
  );

  if (embedded) {
    return modalContent;
  }

  return createPortal(
    <div 
      className={`cargo-modal-overlay ${modalFading ? 'fading' : ''}`}
      onClick={handleOverlayClick}
    >
      {modalContent}
    </div>,
    document.body
  );
};

export default CargoManagementModal;
